import { useState, useEffect, useCallback } from 'react';
import { accountsApi } from '../services/api/endpoints/accounts';
import { settingsApi } from '../services/api/endpoints/settings';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useAuthStore } from '../stores/authStore';
import type {
  ChartOfAccountsInfo,
  InitializationStatus,
  InitializationResult,
  InitializeAccountsDto,
  AccountingFramework
} from '../types/account-initialization';

/**
 * Hook pour gérer l'initialisation des comptes par exercice fiscal
 * 
 * Fonctionnalités:
 * - Récupérer les plans comptables disponibles
 * - Vérifier le statut d'initialisation par exercice
 * - Initialiser les comptes pour un exercice
 * - Réinitialiser avec un nouveau framework
 */
export function useAccountInitialization() {
  const [charts, setCharts] = useState<ChartOfAccountsInfo[]>([]);
  const [statusByFiscalYear, setStatusByFiscalYear] = useState<Record<string, InitializationStatus>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(false);

  const { currentFiscalYear } = useFiscalYearStore();
  const { organization } = useAuthStore();

  /**
   * Charger les plans comptables disponibles
   */
  const loadAvailableCharts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await accountsApi.getAvailableCharts();
      
      if (response.success && response.data) {
        // Gérer le cas où data est enveloppé
        const chartsData = Array.isArray(response.data) 
          ? response.data 
          : (response.data as any).data || [];
        setCharts(chartsData);
      } else {
        // Fallback avec les données statiques si l'API échoue
        console.warn('⚠️ Fallback vers données statiques pour les charts');
        setCharts([
          {
            framework: 'SYSCOHADA_NORMAL',
            name: 'SYSCOHADA - Système Normal',
            description: 'Plan comptable complet pour les entreprises avec CA ≥ 100M FCFA',
            accountCount: 491
          },
          {
            framework: 'SYSCOHADA_MINIMAL',
            name: 'SYSCOHADA - Système Minimal',
            description: 'Plan comptable simplifié pour les TPE avec CA < 100M FCFA',
            accountCount: 147
          },
          {
            framework: 'IFRS',
            name: 'IFRS - International',
            description: 'Plan comptable basé sur les normes IFRS internationales',
            accountCount: 225
          }
        ]);
      }
    } catch (err) {
      console.error('Erreur chargement des charts:', err);
      setError('Impossible de charger les plans comptables disponibles');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Vérifier le statut d'initialisation d'un exercice fiscal
   */
  const checkInitializationStatus = useCallback(async (fiscalYearId: string): Promise<InitializationStatus | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await accountsApi.getInitializationStatus(fiscalYearId);
      
      if (response.success && response.data) {
        const status = response.data.data || response.data;
        setStatusByFiscalYear(prev => ({
          ...prev,
          [fiscalYearId]: status
        }));
        return status;
      }
      
      // Si l'API retourne une erreur, considérer comme non initialisé
      const defaultStatus: InitializationStatus = {
        isInitialized: false,
        accountCount: 0,
        framework: null,
        fiscalYearId
      };
      setStatusByFiscalYear(prev => ({
        ...prev,
        [fiscalYearId]: defaultStatus
      }));
      return defaultStatus;
    } catch (err) {
      console.error('Erreur vérification statut:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Initialiser les comptes pour l'exercice fiscal courant
   */
  const initializeAccounts = useCallback(async (
    framework: AccountingFramework,
    options?: {
      overwrite?: boolean;
      postableOnly?: boolean;
      classFilter?: string[];
    }
  ): Promise<InitializationResult | null> => {
    if (!currentFiscalYear) {
      setError('Aucun exercice fiscal sélectionné');
      return null;
    }

    if (!organization?.id) {
      setError('Organisation non trouvée');
      return null;
    }

    try {
      setInitializing(true);
      setError(null);

      const payload: InitializeAccountsDto = {
        fiscalYearId: currentFiscalYear.id,
        companyId: organization.id,
        framework,
        overwrite: options?.overwrite ?? false,
        postableOnly: options?.postableOnly ?? false,
        classFilter: options?.classFilter
      };

      console.log('🔄 Initialisation des comptes:', payload);
      const response = await accountsApi.initializeAccounts(payload);

      if (response.success && response.data) {
        const result = response.data.data || response.data;
        console.log('✅ Initialisation réussie:', result);

        // Mettre à jour le statut local
        setStatusByFiscalYear(prev => ({
          ...prev,
          [currentFiscalYear.id]: {
            isInitialized: true,
            accountCount: result.accountsCreated,
            framework: result.framework,
            fiscalYearId: currentFiscalYear.id
          }
        }));

        // Synchroniser silencieusement le plan comptable par défaut dans les settings
        settingsApi.updateSettings({
          category: 'accounting',
          settings: { accountingFramework: framework }
        }).catch(() => { /* non bloquant */ });

        return result;
      } else {
        setError(response.error || 'Erreur lors de l\'initialisation');
        return null;
      }
    } catch (err) {
      console.error('Erreur initialisation:', err);
      setError('Erreur lors de l\'initialisation des comptes');
      return null;
    } finally {
      setInitializing(false);
    }
  }, [currentFiscalYear, organization]);

  /**
   * Réinitialiser les comptes avec un nouveau framework
   */
  const reinitializeAccounts = useCallback(async (
    fiscalYearId: string,
    framework: AccountingFramework
  ): Promise<InitializationResult | null> => {
    if (!organization?.id) {
      setError('Organisation non trouvée');
      return null;
    }

    try {
      setInitializing(true);
      setError(null);

      console.log('🔄 Réinitialisation des comptes:', { fiscalYearId, framework });
      const response = await accountsApi.reinitializeAccounts(fiscalYearId, {
        companyId: organization.id,
        framework
      });

      if (response.success && response.data) {
        const result = response.data.data || response.data;
        console.log('✅ Réinitialisation réussie:', result);

        // Mettre à jour le statut local
        setStatusByFiscalYear(prev => ({
          ...prev,
          [fiscalYearId]: {
            isInitialized: true,
            accountCount: result.accountsCreated,
            framework: result.framework,
            fiscalYearId
          }
        }));

        // Synchroniser silencieusement le plan comptable par défaut dans les settings
        settingsApi.updateSettings({
          category: 'accounting',
          settings: { accountingFramework: framework }
        }).catch(() => { /* non bloquant */ });

        return result;
      } else {
        setError(response.error || 'Erreur lors de la réinitialisation');
        return null;
      }
    } catch (err) {
      console.error('Erreur réinitialisation:', err);
      setError('Erreur lors de la réinitialisation des comptes');
      return null;
    } finally {
      setInitializing(false);
    }
  }, [organization]);

  /**
   * Obtenir le statut pour l'exercice fiscal courant
   */
  const currentStatus = currentFiscalYear?.id 
    ? statusByFiscalYear[currentFiscalYear.id] 
    : null;

  /**
   * Charger le statut de l'exercice courant au montage
   */
  useEffect(() => {
    if (currentFiscalYear?.id && !statusByFiscalYear[currentFiscalYear.id]) {
      checkInitializationStatus(currentFiscalYear.id);
    }
  }, [currentFiscalYear?.id, checkInitializationStatus, statusByFiscalYear]);

  /**
   * Charger les charts au montage
   */
  useEffect(() => {
    if (charts.length === 0) {
      loadAvailableCharts();
    }
  }, [charts.length, loadAvailableCharts]);

  return {
    // Données
    charts,
    statusByFiscalYear,
    currentStatus,
    currentFiscalYear,
    
    // État
    loading,
    initializing,
    error,
    
    // Actions
    loadAvailableCharts,
    checkInitializationStatus,
    initializeAccounts,
    reinitializeAccounts,
    
    // Helpers
    isCurrentFiscalYearInitialized: currentStatus?.isInitialized ?? false,
    currentFramework: currentStatus?.framework ?? null
  };
}
